import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack } from '@mui/material';
import { useReactFlow } from '@xyflow/react';

import { api, isDefined } from 'common';

import { ConditionCard } from './ConditionCard';
import { getNewCondition } from './constants';
import { useReactFlowViewContext } from '../../../context/context';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

interface ConditionalFormFieldsProps {
  fieldNamePrefix?: string;
  conditionalTypeFieldLabel?: string;
  matchAllLabel?: string;
  matchFirstLabel?: string;
}

export function ConditionalFormFields(props: ConditionalFormFieldsProps) {
  const {
    fieldNamePrefix = 'config',
    conditionalTypeFieldLabel = 'Path options',
    matchAllLabel = 'Follow all matching conditions',
    matchFirstLabel = 'Follow first matching condition only',
  } = props;
  const conditionsFieldName = `${fieldNamePrefix}.conditions`;
  const conditionalTypeFieldName = `${fieldNamePrefix}.conditionalType`;

  const { value: conditions = [], setValue } =
    useSelectedNodeField<api.FlowDefinition.Conditional.ConditionalBlockConfig['conditions']>(
      conditionsFieldName
    );
  const { edges, reactFlowMutators, selectedNodeId } = useReactFlowViewContext();
  const { updateNode } = useReactFlow();

  const handleReorder = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }
    const newConditions = [...conditions];
    const [movedCondition] = newConditions.splice(fromIndex, 1);
    newConditions.splice(toIndex, 0, movedCondition);
    setValue(newConditions);

    // Refresh the node edges
    if (isDefined(selectedNodeId)) {
      updateNode(selectedNodeId, (node) => ({
        ...node,
        measured: undefined,
      }));
    }
  };

  return (
    <Stack gap={4}>
      <BlockSelectField
        fieldName={conditionalTypeFieldName}
        label={conditionalTypeFieldLabel}
        options={[
          {
            label: matchAllLabel,
            value: api.FlowDefinition.Conditional.ConditionalType.MatchAll,
          },
          {
            label: matchFirstLabel,
            value: api.FlowDefinition.Conditional.ConditionalType.MatchFirst,
          },
        ]}
      />
      <Stack gap={4}>
        {conditions.map((condition, index) => {
          return (
            <ConditionCard
              key={condition.id}
              conditionId={condition.id}
              index={index}
              fieldNamePrefix={`${conditionsFieldName}.${index}`}
              onReorder={handleReorder}
              onDelete={() => {
                if (!isDefined(reactFlowMutators)) {
                  return;
                }
                const affectedEdgeIds = edges
                  .filter((edge) => edge.sourceHandle === condition.id)
                  .map((edge) => edge.id);
                reactFlowMutators.deleteEdges(affectedEdgeIds);
                setValue(conditions.filter((_, i) => i !== index));
              }}
            />
          );
        })}
      </Stack>
      <Box>
        <Button
          variant="outlined"
          onClick={() => {
            setValue([...conditions, getNewCondition()]);
          }}
          startIcon={<AddIcon />}
        >
          Add Condition
        </Button>
      </Box>
    </Stack>
  );
}
